import { useEffect, useMemo, useState } from 'react';
import '../styles/CuentasPagar.css';
import { finanzasService } from '../services/api';

const initialFiltros = {
  estado: 'todas',
  suplidor_id: '',
  fecha_desde: '',
  fecha_hasta: '',
  prioridad: ''
};

const initialCuentaForm = {
  numero_factura: '',
  suplidor_id: '',
  concepto: '',
  fecha_emision: '',
  fecha_vencimiento: '',
  monto_original: '',
  prioridad: 'media',
  notas: ''
};

const initialGastoFijoForm = {
  nombre: '',
  descripcion: '',
  monto: '',
  moneda: 'DOP',
  frecuencia: 'mensual',
  intervalo: 1,
  dia_cobro: '',
  suplidor_id: '',
  tipo_egreso: '',
  notas: ''
};

const initialSuplidorForm = {
  nombre: '',
  rnc: '',
  telefono: '',
  correo: '',
  direccion: ''
};

const estadosOpciones = [
  { value: 'todas', label: 'Todas' },
  { value: 'pendiente', label: 'Pendientes' },
  { value: 'parcial', label: 'Pago parcial' },
  { value: 'vencida', label: 'Vencidas' },
  { value: 'pagada', label: 'Pagadas' }
];

const prioridadesOpciones = [
  { value: '', label: 'Todas' },
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Media' },
  { value: 'baja', label: 'Baja' }
];

const frecuenciasOpciones = [
  { value: 'mensual', label: 'Mensual' },
  { value: 'quincenal', label: 'Quincenal' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'diario', label: 'Diario' },
  { value: 'anual', label: 'Anual' },
  { value: 'personalizado', label: 'Personalizado (días)' }
];

const monedasOpciones = [
  { value: 'DOP', label: 'Peso dominicano (DOP)' },
  { value: 'USD', label: 'Dólar estadounidense (USD)' }
];

const formatearMoneda = (monto, moneda = 'DOP') => {
  const currency = moneda === 'USD' ? 'USD' : 'DOP';
  const locale = currency === 'USD' ? 'en-US' : 'es-DO';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2
  }).format(monto || 0);
};

const formatearFecha = (fecha) => {
  if (!fecha) return 'N/A';
  const parts = fecha.split('-');
  if (parts.length !== 3) return fecha;
  const [year, month, day] = parts;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(date.getTime())) return fecha;
  return date.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const obtenerClaseEstado = (estado) => {
  switch (estado) {
    case 'pendiente':
      return 'pendiente';
    case 'vencida':
      return 'vencida';
    case 'parcial':
      return 'parcial';
    case 'pagada':
      return 'pagada';
    default:
      return 'pendiente';
  }
};

const obtenerBadgePrioridad = (prioridad) => {
  switch (prioridad) {
    case 'alta':
      return 'badge-alta';
    case 'media':
      return 'badge-media';
    case 'baja':
      return 'badge-baja';
    default:
      return 'badge-media';
  }
};

const calcularResumen = (cuentas = []) => {
  const total_cuentas = cuentas.length;
  const total_pendiente = cuentas.reduce((acc, cuenta) => acc + (cuenta.monto_pendiente || 0), 0);
  const cuentas_vencidas = cuentas.filter((cuenta) => (cuenta.dias_restantes ?? 0) < 0);
  const total_vencido = cuentas_vencidas.reduce(
    (acc, cuenta) => acc + (cuenta.monto_pendiente || 0),
    0
  );
  const cuentas_pagadas = cuentas.filter((cuenta) => cuenta.estado === 'pagada');
  const cuentas_pendientes = cuentas.filter((cuenta) => cuenta.estado !== 'pagada');

  return {
    total_cuentas,
    total_pendiente,
    total_vencido,
    cuentas_vencidas: cuentas_vencidas.length,
    cuentas_pagadas: cuentas_pagadas.length,
    cuentas_pendientes: cuentas_pendientes.length
  };
};

const calcularVencimientos = (cuentas = []) => {
  const toAlertItem = (cuenta) => ({
    id: cuenta.id,
    factura: cuenta.numero_factura || 'Sin factura',
    suplidor: cuenta.suplidor_nombre || 'N/A',
    monto: cuenta.monto_pendiente || 0,
    dias: cuenta.dias_restantes ?? 0
  });

  return {
    vencidas: cuentas
      .filter((cuenta) => (cuenta.dias_restantes ?? 0) < 0)
      .map(toAlertItem),
    proximos_7_dias: cuentas
      .filter((cuenta) => {
        const dias = cuenta.dias_restantes ?? null;
        return dias !== null && dias >= 0 && dias <= 7;
      })
      .map(toAlertItem),
    proximos_15_dias: cuentas
      .filter((cuenta) => {
        const dias = cuenta.dias_restantes ?? null;
        return dias !== null && dias > 7 && dias <= 15;
      })
      .map(toAlertItem)
  };
};

const CuentasPagar = () => {
  const [cuentasPagar, setCuentasPagar] = useState([]);
  const [resumenCuentas, setResumenCuentas] = useState(null);
  const [reporteVencimientos, setReporteVencimientos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtros, setFiltros] = useState(initialFiltros);
  const [paginacion, setPaginacion] = useState({
    total: 0,
    pages: 0,
    current_page: 1,
    per_page: 20
  });
  const [suplidores, setSuplidores] = useState([]);
  const [gastosFijos, setGastosFijos] = useState([]);
  const [formCuenta, setFormCuenta] = useState(initialCuentaForm);
  const [formGastoFijo, setFormGastoFijo] = useState(initialGastoFijoForm);
  const [formSuplidor, setFormSuplidor] = useState(initialSuplidorForm);
  const [mostrarFormCuenta, setMostrarFormCuenta] = useState(false);
  const [mostrarFormGastoFijo, setMostrarFormGastoFijo] = useState(false);
  const [mostrarFormSuplidor, setMostrarFormSuplidor] = useState(false);

  const paramsConsulta = useMemo(() => {
    const params = {
      page: paginacion.current_page,
      per_page: paginacion.per_page
    };
    if (filtros.estado && filtros.estado !== 'todas') params.estado = filtros.estado;
    if (filtros.suplidor_id) params.suplidor_id = filtros.suplidor_id;
    if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
    if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
    if (filtros.prioridad) params.prioridad = filtros.prioridad;
    return params;
  }, [filtros, paginacion.current_page, paginacion.per_page]);

  const cargarDatos = async () => {
    setLoading(true);
    setError(null);
    try {
      const [cuentasResp, suplidoresResp, gastosResp] = await Promise.all([
        finanzasService.getCuentasPagar(paramsConsulta),
        finanzasService.getSuplidores({ activo: true }),
        finanzasService.getGastosFijos({})
      ]);

      const cuentas = cuentasResp.data?.cuentas || [];
      setCuentasPagar(cuentas);
      setResumenCuentas(calcularResumen(cuentas));
      setReporteVencimientos(calcularVencimientos(cuentas));
      setPaginacion((prev) => ({
        ...prev,
        total: cuentasResp.data?.total || 0,
        pages: cuentasResp.data?.pages || 0,
        current_page: cuentasResp.data?.current_page || 1
      }));
      setSuplidores(suplidoresResp.data?.suplidores || []);
      setGastosFijos(gastosResp.data?.gastos_fijos || []);
    } catch (err) {
      console.error('Error al cargar cuentas por pagar', err);
      setError(err.response?.data?.error || 'Error al cargar la información');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [paramsConsulta]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({
      ...prev,
      [campo]: valor
    }));
    setPaginacion((prev) => ({ ...prev, current_page: 1 }));
  };

  const limpiarFiltros = () => {
    setFiltros(initialFiltros);
    setPaginacion((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleCrearSuplidor = async (e) => {
    e.preventDefault();
    try {
      await finanzasService.createSuplidor(formSuplidor);
      setFormSuplidor(initialSuplidorForm);
      setMostrarFormSuplidor(false);
      await cargarDatos();
    } catch (err) {
      console.error('Error creando suplidor', err);
      setError(err.response?.data?.error || 'No se pudo crear el suplidor');
    }
  };

  const handleCrearCuenta = async (e) => {
    e.preventDefault();
    try {
      await finanzasService.createCuentaPagar({
        ...formCuenta,
        monto_original: Number(formCuenta.monto_original || 0),
        monto_pendiente: Number(formCuenta.monto_original || 0)
      });
      setFormCuenta(initialCuentaForm);
      setMostrarFormCuenta(false);
      await cargarDatos();
    } catch (err) {
      console.error('Error creando cuenta por pagar', err);
      setError(err.response?.data?.error || 'No se pudo crear la cuenta por pagar');
    }
  };

  const handleCrearGastoFijo = async (e) => {
    e.preventDefault();
    try {
      await finanzasService.createGastoFijo({
        ...formGastoFijo,
        monto: Number(formGastoFijo.monto || 0),
        intervalo: Number(formGastoFijo.intervalo || 1),
        dia_cobro: formGastoFijo.dia_cobro ? Number(formGastoFijo.dia_cobro) : null,
        moneda: formGastoFijo.moneda || 'DOP'
      });
      setFormGastoFijo(initialGastoFijoForm);
      setMostrarFormGastoFijo(false);
      await cargarDatos();
    } catch (err) {
      console.error('Error creando gasto fijo', err);
      setError(err.response?.data?.error || 'No se pudo crear el gasto fijo');
    }
  };

  const handleGenerarDesdeGastoFijo = async (gasto) => {
    try {
      const hoy = new Date();
      const yyyy = hoy.getFullYear();
      const mm = String(hoy.getMonth() + 1).padStart(2, '0');
      const dd = String(hoy.getDate()).padStart(2, '0');
      const hoyStr = `${yyyy}-${mm}-${dd}`;

      const frecuencia = gasto?.frecuencia?.toLowerCase?.() || '';
      const proximaGeneracionDate = gasto?.proxima_generacion ? new Date(gasto.proxima_generacion) : null;
      const mismaFechaExacta = gasto?.proxima_generacion === hoyStr;

      if (frecuencia === 'mensual') {
        if (proximaGeneracionDate) {
          const mismaVentanaMensual =
            proximaGeneracionDate.getFullYear() === hoy.getFullYear() &&
            proximaGeneracionDate.getMonth() === hoy.getMonth();
          if (!mismaVentanaMensual) {
            window.alert(
              `Este gasto fijo mensual solo puede generar cuenta durante ${formatearFecha(
                gasto?.proxima_generacion
              )}.`
            );
            return;
          }
        }
      } else if (frecuencia === 'quincenal') {
        if (!mismaFechaExacta) {
          window.alert(
            `Este gasto fijo solo puede generar cuenta el ${formatearFecha(
              gasto?.proxima_generacion
            )}.`
          );
          return;
        }

        const diaMes = hoy.getDate();
        if (diaMes !== 15 && diaMes !== 30) {
          window.alert('Los gastos fijos quincenales solo se generan los días 15 y 30 de cada mes.');
          return;
        }
      } else {
        if (!mismaFechaExacta) {
          window.alert(
            `Este gasto fijo solo puede generar cuenta el ${formatearFecha(
              gasto?.proxima_generacion
            )}.`
          );
          return;
        }
      }

      await finanzasService.generarDesdeGastoFijo(gasto.id, {});
      await cargarDatos();
    } catch (err) {
      console.error('Error generando cuenta desde gasto fijo', err);
      setError(err.response?.data?.error || 'No se pudo generar la cuenta');
    }
  };

  const handlePagarCuenta = async (cuentaId) => {
    const monto = window.prompt('Ingrese el monto pagado (deje vacío para pagar el total):');
    try {
      await finanzasService.pagarCuentaPagar(cuentaId, {
        monto_pago: monto ? Number(monto) : undefined
      });
      await cargarDatos();
    } catch (err) {
      console.error('Error registrando pago', err);
      setError(err.response?.data?.error || 'No se pudo registrar el pago');
    }
  };

  const cambiarPagina = (nuevaPagina) => {
    if (nuevaPagina < 1 || (paginacion.pages && nuevaPagina > paginacion.pages)) return;
    setPaginacion((prev) => ({ ...prev, current_page: nuevaPagina }));
  };

  return (
    <div className="clientes-page">
      <div className="clientes-header">
        <h1>Cuentas por Pagar</h1>
        <div className="header-actions">
          <button className="btn" onClick={() => setMostrarFormCuenta((prev) => !prev)}>
            + Nueva cuenta
          </button>
          <button className="btn secondary" onClick={() => setMostrarFormGastoFijo((prev) => !prev)}>
            + Gasto fijo
          </button>
          <button className="btn secondary" onClick={() => setMostrarFormSuplidor((prev) => !prev)}>
            + Suplidor
          </button>
        </div>
      </div>

      {error && (
        <div className="banner error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {resumenCuentas && (
        <div className="cuentas-resumen">
          <div className="resumen-card">
            <div className="resumen-title">Total Cuentas</div>
            <div className="resumen-value">{resumenCuentas.total_cuentas}</div>
            <div className="resumen-subtitle">Registradas</div>
          </div>
          <div className="resumen-card">
            <div className="resumen-title">Pendiente de pago</div>
            <div className="resumen-value">{formatearMoneda(resumenCuentas.total_pendiente)}</div>
            <div className="resumen-subtitle">
              {resumenCuentas.cuentas_pendientes} cuentas por pagar
            </div>
          </div>
          <div className="resumen-card">
            <div className="resumen-title">Vencido</div>
            <div className="resumen-value vencido">{formatearMoneda(resumenCuentas.total_vencido)}</div>
            <div className="resumen-subtitle">{resumenCuentas.cuentas_vencidas} facturas vencidas</div>
          </div>
          <div className="resumen-card">
            <div className="resumen-title">Pagadas</div>
            <div className="resumen-value pagado">{resumenCuentas.cuentas_pagadas}</div>
            <div className="resumen-subtitle">Cuentas liquidadas</div>
          </div>
        </div>
      )}

      {reporteVencimientos && (
        <div className="alertas-vencimiento">
          <h2>Alertas de Vencimiento</h2>
          <div className="alertas-grid">
            <div className="alerta-card vencidas">
              <div className="alerta-header">⚠️ Vencidas</div>
              <div className="alerta-body">
                <div className="alerta-count">{reporteVencimientos.vencidas.length}</div>
                <div className="alerta-label">Facturas vencidas</div>
              </div>
              {reporteVencimientos.vencidas.map((alerta) => (
                <div key={`vencida-${alerta.id}`} className="alerta-item">
                  <div>
                    <strong>{alerta.factura}</strong>
                    <div>{alerta.suplidor}</div>
                  </div>
                  <div>
                    <div>{formatearMoneda(alerta.monto)}</div>
                    <span className="badge vencida">{Math.abs(alerta.dias)} días</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="alerta-card proximos">
              <div className="alerta-header">⏳ Próximos 7 días</div>
              <div className="alerta-body">
                <div className="alerta-count">{reporteVencimientos.proximos_7_dias.length}</div>
                <div className="alerta-label">Vencimientos inminentes</div>
              </div>
              {reporteVencimientos.proximos_7_dias.map((alerta) => (
                <div key={`prox7-${alerta.id}`} className="alerta-item">
                  <div>
                    <strong>{alerta.factura}</strong>
                    <div>{alerta.suplidor}</div>
                  </div>
                  <div>
                    <div>{formatearMoneda(alerta.monto)}</div>
                    <span className="badge proximos">{alerta.dias} días</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="alerta-card quincena">
              <div className="alerta-header">📅 Próximos 15 días</div>
              <div className="alerta-body">
                <div className="alerta-count">{reporteVencimientos.proximos_15_dias.length}</div>
                <div className="alerta-label">Planifica tus pagos</div>
              </div>
              {reporteVencimientos.proximos_15_dias.map((alerta) => (
                <div key={`prox15-${alerta.id}`} className="alerta-item">
                  <div>
                    <strong>{alerta.factura}</strong>
                    <div>{alerta.suplidor}</div>
                  </div>
                  <div>
                    <div>{formatearMoneda(alerta.monto)}</div>
                    <span className="badge quincena">{alerta.dias} días</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="panel-contenido">
        <div className="panel-filtros">
          <h3>Filtros</h3>
          <div className="filtros-grid">
            <div className="form-group">
              <label>Estado</label>
              <select
                value={filtros.estado}
                onChange={(e) => handleFiltroChange('estado', e.target.value)}
              >
                {estadosOpciones.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Prioridad</label>
              <select
                value={filtros.prioridad}
                onChange={(e) => handleFiltroChange('prioridad', e.target.value)}
              >
                {prioridadesOpciones.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Suplidor</label>
              <select
                value={filtros.suplidor_id}
                onChange={(e) => handleFiltroChange('suplidor_id', e.target.value)}
              >
                <option value="">Todos</option>
                {suplidores.map((suplidor) => (
                  <option key={suplidor.id} value={suplidor.id}>
                    {suplidor.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Desde</label>
              <input
                type="date"
                value={filtros.fecha_desde}
                onChange={(e) => handleFiltroChange('fecha_desde', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Hasta</label>
              <input
                type="date"
                value={filtros.fecha_hasta}
                onChange={(e) => handleFiltroChange('fecha_hasta', e.target.value)}
              />
            </div>
          </div>
          <div className="filtros-actions">
            <button className="btn secondary" onClick={limpiarFiltros}>
              Limpiar filtros
            </button>
            <button className="btn" onClick={cargarDatos} disabled={loading}>
              Actualizar
            </button>
          </div>
        </div>

        <div className="panel-listado">
          <h2>Lista de cuentas por pagar</h2>
          {loading ? (
            <div className="loading">Cargando cuentas...</div>
          ) : (
            <div className="table-responsive">
              <table className="table cuentas-table">
                <thead>
                  <tr>
                    <th>Estado</th>
                    <th>Factura</th>
                    <th>Suplidor</th>
                    <th>Concepto</th>
                    <th>Emisión</th>
                    <th>Vencimiento</th>
                    <th>Pendiente</th>
                    <th>Prioridad</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cuentasPagar.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center">
                        No se encontraron cuentas registradas
                      </td>
                    </tr>
                  ) : (
                    cuentasPagar.map((cuenta) => (
                      <tr key={cuenta.id}>
                        <td>
                          <span className={`badge ${obtenerClaseEstado(cuenta.estado)}`}>
                            {cuenta.estado}
                          </span>
                        </td>
                        <td>
                          <strong>{cuenta.numero_factura || 'N/A'}</strong>
                        </td>
                        <td>{cuenta.suplidor_nombre || 'N/A'}</td>
                        <td>{cuenta.concepto}</td>
                        <td>{formatearFecha(cuenta.fecha_emision)}</td>
                        <td>
                          <div>{formatearFecha(cuenta.fecha_vencimiento)}</div>
                          {cuenta.dias_restantes != null && (
                            <div className="dias-wrapper">
                              {cuenta.dias_restantes < 0 ? (
                                <span className="badge vencida">
                                  {Math.abs(cuenta.dias_restantes)} días vencida
                                </span>
                              ) : (
                                <span className="badge proximos">
                                  Vence en {cuenta.dias_restantes} días
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td>{formatearMoneda(cuenta.monto_pendiente)}</td>
                        <td>
                          <span className={`badge ${obtenerBadgePrioridad(cuenta.prioridad)}`}>
                            {cuenta.prioridad}
                          </span>
                        </td>
                        <td>
                          <div className="actions">
                            {cuenta.estado !== 'pagada' && (
                              <button
                                className="btn-icon"
                                title="Registrar pago"
                                onClick={() => handlePagarCuenta(cuenta.id)}
                              >
                                💳
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {paginacion.pages > 1 && (
            <div className="pagination">
              <button
                className="btn secondary"
                onClick={() => cambiarPagina(paginacion.current_page - 1)}
                disabled={paginacion.current_page <= 1}
              >
                ← Anterior
              </button>
              <span>
                Página {paginacion.current_page} de {paginacion.pages}
              </span>
              <button
                className="btn secondary"
                onClick={() => cambiarPagina(paginacion.current_page + 1)}
                disabled={paginacion.current_page >= paginacion.pages}
              >
                Siguiente →
              </button>
            </div>
          )}
        </div>
      </div>

      {(mostrarFormSuplidor || mostrarFormCuenta || mostrarFormGastoFijo) && (
        <div className="formularios-grid">
          {mostrarFormSuplidor && (
            <form className="form-card" onSubmit={handleCrearSuplidor}>
              <div className="form-card-header">
                <h3>Nuevo Suplidor</h3>
                <button type="button" className="close-btn" onClick={() => setMostrarFormSuplidor(false)}>
                  ✕
                </button>
              </div>
              <div className="form-group">
                <label>Nombre *</label>
                <input
                  required
                  value={formSuplidor.nombre}
                  onChange={(e) => setFormSuplidor((prev) => ({ ...prev, nombre: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>RNC</label>
                <input
                  value={formSuplidor.rnc}
                  onChange={(e) => setFormSuplidor((prev) => ({ ...prev, rnc: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Teléfono</label>
                <input
                  value={formSuplidor.telefono}
                  onChange={(e) => setFormSuplidor((prev) => ({ ...prev, telefono: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Correo</label>
                <input
                  value={formSuplidor.correo}
                  onChange={(e) => setFormSuplidor((prev) => ({ ...prev, correo: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Dirección</label>
                <textarea
                  value={formSuplidor.direccion}
                  onChange={(e) => setFormSuplidor((prev) => ({ ...prev, direccion: e.target.value }))}
                />
              </div>
              <button className="btn" type="submit">
                Guardar suplidor
              </button>
            </form>
          )}

          {mostrarFormCuenta && (
            <form className="form-card" onSubmit={handleCrearCuenta}>
              <div className="form-card-header">
                <h3>Nueva Cuenta por Pagar</h3>
                <button type="button" className="close-btn" onClick={() => setMostrarFormCuenta(false)}>
                  ✕
                </button>
              </div>
              <div className="form-group">
                <label>Concepto *</label>
                <input
                  required
                  value={formCuenta.concepto}
                  onChange={(e) => setFormCuenta((prev) => ({ ...prev, concepto: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Monto *</label>
                <input
                  required
                  type="number"
                  min="1"
                  value={formCuenta.monto_original}
                  onChange={(e) => setFormCuenta((prev) => ({ ...prev, monto_original: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Suplidor</label>
                <select
                  value={formCuenta.suplidor_id}
                  onChange={(e) => setFormCuenta((prev) => ({ ...prev, suplidor_id: e.target.value }))}
                >
                  <option value="">Selecciona</option>
                  {suplidores.map((suplidor) => (
                    <option key={suplidor.id} value={suplidor.id}>
                      {suplidor.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Fecha de emisión</label>
                <input
                  type="date"
                  value={formCuenta.fecha_emision}
                  onChange={(e) => setFormCuenta((prev) => ({ ...prev, fecha_emision: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Fecha de vencimiento</label>
                <input
                  type="date"
                  value={formCuenta.fecha_vencimiento}
                  onChange={(e) => setFormCuenta((prev) => ({ ...prev, fecha_vencimiento: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Prioridad</label>
                <select
                  value={formCuenta.prioridad}
                  onChange={(e) => setFormCuenta((prev) => ({ ...prev, prioridad: e.target.value }))}
                >
                  <option value="alta">Alta</option>
                  <option value="media">Media</option>
                  <option value="baja">Baja</option>
                </select>
              </div>
              <div className="form-group">
                <label>Notas</label>
                <textarea
                  value={formCuenta.notas}
                  onChange={(e) => setFormCuenta((prev) => ({ ...prev, notas: e.target.value }))}
                />
              </div>
              <button className="btn" type="submit">
                Guardar cuenta
              </button>
            </form>
          )}

          {mostrarFormGastoFijo && (
            <form className="form-card" onSubmit={handleCrearGastoFijo}>
              <div className="form-card-header">
                <h3>Nuevo Gasto Fijo</h3>
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => setMostrarFormGastoFijo(false)}
                >
                  ✕
                </button>
              </div>
              <div className="form-group">
                <label>Nombre *</label>
                <input
                  required
                  value={formGastoFijo.nombre}
                  onChange={(e) => setFormGastoFijo((prev) => ({ ...prev, nombre: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Monto *</label>
                <input
                  required
                  type="number"
                  min="1"
                  value={formGastoFijo.monto}
                  onChange={(e) => setFormGastoFijo((prev) => ({ ...prev, monto: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Moneda *</label>
                <select
                  value={formGastoFijo.moneda}
                  onChange={(e) => setFormGastoFijo((prev) => ({ ...prev, moneda: e.target.value }))}
                >
                  {monedasOpciones.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Frecuencia *</label>
                <select
                  value={formGastoFijo.frecuencia}
                  onChange={(e) => setFormGastoFijo((prev) => ({ ...prev, frecuencia: e.target.value }))}
                >
                  {frecuenciasOpciones.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Intervalo</label>
                <input
                  type="number"
                  min="1"
                  value={formGastoFijo.intervalo}
                  onChange={(e) => setFormGastoFijo((prev) => ({ ...prev, intervalo: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Día de cobro</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={formGastoFijo.dia_cobro}
                  onChange={(e) => setFormGastoFijo((prev) => ({ ...prev, dia_cobro: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Suplidor</label>
                <select
                  value={formGastoFijo.suplidor_id}
                  onChange={(e) => setFormGastoFijo((prev) => ({ ...prev, suplidor_id: e.target.value }))}
                >
                  <option value="">Selecciona</option>
                  {suplidores.map((suplidor) => (
                    <option key={suplidor.id} value={suplidor.id}>
                      {suplidor.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Notas</label>
                <textarea
                  value={formGastoFijo.notas}
                  onChange={(e) => setFormGastoFijo((prev) => ({ ...prev, notas: e.target.value }))}
                />
              </div>
              <button className="btn" type="submit">
                Guardar gasto fijo
              </button>
            </form>
          )}
        </div>
      )}

      {gastosFijos.length > 0 && (
        <div className="panel-gastos-fijos">
          <h2>Gastos fijos activos</h2>
          <div className="gastos-fijos-grid">
            {gastosFijos.map((gasto) => (
              <div key={gasto.id} className="gasto-fijo-card">
                <div className="gasto-fijo-header">
                  <h3>{gasto.nombre}</h3>
                  <div className="badges">
                    <span className="badge">{gasto.frecuencia}</span>
                    <span className="badge">{gasto.moneda || 'DOP'}</span>
                  </div>
                </div>
                <div className="gasto-fijo-body">
                  <div>
                    <strong>Monto:</strong> {formatearMoneda(gasto.monto, gasto.moneda)}
                  </div>
                  <div>
                    <strong>Próxima generación:</strong> {formatearFecha(gasto.proxima_generacion)}
                  </div>
                  <div>
                    <strong>Suplidor:</strong> {gasto.suplidor_nombre || 'N/A'}
                  </div>
                  <div>
                    <strong>Notas:</strong> {gasto.notas || '—'}
                  </div>
                </div>
                <div className="gasto-fijo-actions">
                  <button className="btn" onClick={() => handleGenerarDesdeGastoFijo(gasto)}>
                    Generar cuenta
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CuentasPagar;